/**
 * ContentStandardInspectModal
 *
 * Read-only detail view of a content standard.
 * Available to ALL studio roles (SME, QA, Tenant Admin, System Admin).
 *
 * Shows:
 *   • Overview   — name, description, pedagogical model, language, word count target
 *   • Unit Structure  — ordered sections with weight (%) and derived word budget
 *   • Word Budget — min / max / target from locale_config + dynamic scaling settings
 *   • Assessment Mix — question type → count table
 *
 * Usage:
 *   <ContentStandardInspectModal
 *     show={true}
 *     onHide={() => setShow(false)}
 *     standardId={6}        // triggers fetch
 *     slug="tau-gy-med"
 *     onClone={() => openCloneModal()}  // optional — omit for SME (no clone button)
 *   />
 */
import React, { useState, useEffect } from 'react';
import {
    Modal, Nav, Spinner, Alert, Badge, Table, Button, ProgressBar
} from 'react-bootstrap';
import { getContentStandardDetail } from '../../api/api';

// ── helpers ───────────────────────────────────────────────────────────────────

const humanise = s => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const pct = w => `${Math.round((w || 0) * 100)}%`;

const BLOOM_COLOURS = {
    remember: 'info',    understand: 'primary', apply: 'success',
    analyze: 'warning',  evaluate: 'danger',    create: 'dark',
};

const SECTION_HINTS = {
    learning_objectives:    'States what the student will be able to do after completing this unit.',
    introduction:           'Contextual framing — relevance, real-world importance, academic context.',
    concept_explanation:    'Core content: definitions, theory, worked derivations.',
    examples:               'Worked examples grounded in the concept and source material.',
    real_world_application: 'Industry or professional scenarios applying the concept.',
    check_your_progress:    'Formative self-assessment questions (not graded).',
    summary:                'Concise bullet-point recap of the unit\'s key ideas.',
    conclusion:             'Keywords, closing thoughts, or cross-references.',
    activity_or_case:       'Optional: case study, design activity, or problem set.',
    consolidate:            'Synthesis section that integrates all concepts of the unit.',
};


// ── Main component ────────────────────────────────────────────────────────────

export default function ContentStandardInspectModal({
    show, onHide, standardId, slug, onClone
}) {
    const [tab, setTab] = useState('overview');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show || !standardId || !slug) return;
        setLoading(true);
        setError(null);
        setData(null);
        getContentStandardDetail(slug, standardId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load content standard details.'))
            .finally(() => setLoading(false));
    }, [show, standardId, slug]);

    // ── derived data ──────────────────────────────────────────────────────────

    const unitStructure = (() => {
        const me = data?.mandatory_elements || {};
        if (Array.isArray(me.unit_structure)) return me.unit_structure;
        return [];
    })();

    const totalWords = data?.word_count_target || 4500;
    const weightSum  = unitStructure.reduce((a, s) => a + (s.weight || 0), 0);

    const wordBudget = (() => {
        const lc = data?.locale_config || {};
        const st = lc.structure || {};
        const uw = st.unit_word_count || {};
        const ds = st.dynamic_word_scaling || {};
        return { min: uw.min, max: uw.max, target: uw.target, dynamicEnabled: !!ds.enabled, baseWords: ds.base_words_per_topic };
    })();

    // Unit-level word budget for the "≈ Words" column in the section table.
    // locale_config.structure.unit_word_count.target is the per-unit budget.
    // data.word_count_target is the total project-level target (not per-unit).
    const unitWordBudget = wordBudget.target ?? wordBudget.max ?? data?.word_count_target ?? 4500;

    // Flatten the assessment_mix: values may be plain numbers OR nested objects.
    // We normalise to a flat list of {key, value: string | number} for safe rendering.
    const assessmentMix = Object.entries(data?.assessment_mix || {}).map(([k, v]) => {
        if (v === null || v === undefined) return { key: k, display: '—', detail: null };
        if (typeof v === 'object') {
            // Nested object — render as a readable key:value list
            const detail = Object.entries(v)
                .map(([ek, ev]) => `${humanise(ek)}: ${ev}`)
                .join(', ');
            return { key: k, display: null, detail };
        }
        return { key: k, display: String(v), detail: null };
    });

    // ── render ────────────────────────────────────────────────────────────────

    const tabs = [
        { key: 'overview',   label: 'Overview' },
        { key: 'structure',  label: 'Unit Structure' },
        { key: 'budget',     label: 'Word Budget' },
        { key: 'assessment', label: 'Assessment Mix' },
    ];

    return (
        <Modal show={show} onHide={onHide} size="lg" centered aria-labelledby="cs-inspect-title">
            <Modal.Header closeButton>
                <Modal.Title id="cs-inspect-title" style={{ fontSize: 16 }}>
                    Content Standard Details
                    {data && <Badge bg="light" text="dark" className="ms-2 border" style={{ fontSize: 12 }}>
                        {data.tenant_id ? '🏛 Tenant Custom' : '🌐 Global'}
                    </Badge>}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0" style={{ minHeight: 400 }}>
                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" size="sm" aria-hidden="true" />
                        <p className="text-muted small mt-2">Loading standard…</p>
                    </div>
                )}
                {error && <Alert variant="danger" role="alert" className="m-3">{error}</Alert>}

                {data && (
                    <>
                        {/* Tab bar */}
                        <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="px-3 pt-2 border-bottom">
                            {tabs.map(t => (
                                <Nav.Item key={t.key}>
                                    <Nav.Link eventKey={t.key} style={{ fontSize: 13 }}>{t.label}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>

                        <div className="p-4">
                            {/* ── Overview ── */}
                            {tab === 'overview' && (
                                <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                                    <tbody>
                                        <tr><th scope="row" style={{ width: '34%', background: '#f9fafb' }}>Name</th><td>{data.name}</td></tr>
                                        <tr><th scope="row" style={{ background: '#f9fafb' }}>Description</th><td>{data.description || <span className="text-muted">—</span>}</td></tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>Pedagogical Model</th>
                                            <td>
                                                <Badge bg="primary">{data.pedagogical_model || 'ARCS'}</Badge>
                                                <span className="text-muted small ms-2">
                                                    {data.pedagogical_model === 'ARCS'
                                                        ? '— Attention, Relevance, Confidence, Satisfaction'
                                                        : ''}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr><th scope="row" style={{ background: '#f9fafb' }}>Language Variant</th><td>{humanise(data.language_variant)}</td></tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>Project Word Count Target</th>
                                            <td>
                                                <strong>{(data.word_count_target || 4500).toLocaleString()}</strong> words
                                                <span className="text-muted small ms-2">(total across all units in the project)</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>Per-Unit Word Budget</th>
                                            <td>
                                                {wordBudget.target != null
                                                    ? <><strong>{wordBudget.target.toLocaleString()}</strong> words target (min {wordBudget.min?.toLocaleString() ?? '—'} / max {wordBudget.max?.toLocaleString() ?? '—'})</>
                                                    : <span className="text-muted small">Set in Word Budget tab of locale_config</span>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>Scope</th>
                                            <td>
                                                {data.tenant_id
                                                    ? <><Badge bg="warning" text="dark">Tenant-specific</Badge><span className="text-muted small ms-2">Available only to your institution</span></>
                                                    : <><Badge bg="secondary">Global</Badge><span className="text-muted small ms-2">Shared across all institutions</span></>}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}

                            {/* ── Unit Structure ── */}
                            {tab === 'structure' && (
                                <>
                                    {unitStructure.length === 0
                                        ? <Alert variant="info" role="status">No unit structure defined in this standard.</Alert>
                                        : (
                                        <>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <p className="text-muted small mb-0">
                                                    Each row is a content section generated by the AI for every unit.
                                                    <strong> Weight</strong> determines the share of the <strong>per-unit</strong> word budget (≈ {unitWordBudget.toLocaleString()} words) allocated to that section.
                                                </p>
                                                <Badge bg={Math.abs(weightSum - 1) < 0.01 ? 'success' : 'danger'}>
                                                    Total weight: {pct(weightSum)}
                                                </Badge>
                                            </div>
                                            <Table size="sm" bordered responsive style={{ fontSize: 13 }}>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th scope="col">#</th>
                                                        <th scope="col">Section Name</th>
                                                        <th scope="col">Purpose</th>
                                                        <th scope="col" className="text-center">Weight</th>
                                                        <th scope="col" className="text-center">≈ Words</th>
                                                        <th scope="col" style={{ width: 120 }}>Distribution</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {unitStructure.map((s, i) => {
                                                        const w = s.weight || 0;
                                                        const words = Math.round(unitWordBudget * w);
                                                        return (
                                                            <tr key={i}>
                                                                <td className="text-muted">{i + 1}</td>
                                                                <td><code style={{ fontSize: 12 }}>{s.name}</code></td>
                                                                <td className="text-muted small">{SECTION_HINTS[s.name] || humanise(s.name)}</td>
                                                                <td className="text-center fw-semibold">{pct(w)}</td>
                                                                <td className="text-center text-muted">{words.toLocaleString()}</td>
                                                                <td>
                                                                    <ProgressBar
                                                                        now={w * 100}
                                                                        style={{ height: 8 }}
                                                                        variant={w > 0.3 ? 'primary' : w > 0.1 ? 'info' : 'secondary'}
                                                                        aria-label={`${pct(w)} of total`}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </>
                                    )}
                                </>
                            )}

                            {/* ── Word Budget ── */}
                            {tab === 'budget' && (
                                <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                                    <tbody>
                                        <tr>
                                            <th scope="row" style={{ width: '44%', background: '#f9fafb' }}>Minimum words per unit</th>
                                            <td>{wordBudget.min != null ? wordBudget.min.toLocaleString() : '—'}</td>
                                        </tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>Target words per unit</th>
                                            <td><strong>{wordBudget.target != null ? wordBudget.target.toLocaleString() : (data.word_count_target || 4500).toLocaleString()}</strong></td>
                                        </tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>Maximum words per unit</th>
                                            <td>{wordBudget.max != null ? wordBudget.max.toLocaleString() : '—'}</td>
                                        </tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>Dynamic word scaling</th>
                                            <td>
                                                {wordBudget.dynamicEnabled
                                                    ? <><Badge bg="success">Enabled</Badge><span className="text-muted small ms-2">{wordBudget.baseWords} words per topic, scaled by topic count</span></>
                                                    : <Badge bg="secondary">Disabled</Badge>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th scope="row" style={{ background: '#f9fafb' }}>How it works</th>
                                            <td className="text-muted small">
                                                Each unit's total budget is split across sections using the weights in the Unit Structure tab.
                                                E.g., a section with 35% weight in a 4,500-word unit receives ≈ 1,575 words.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}

                            {/* ── Assessment Mix ── */}
                            {tab === 'assessment' && (
                                assessmentMix.length === 0
                                    ? <Alert variant="info" role="status">No assessment mix defined in this standard.</Alert>
                                    : (
                                    <>
                                        <p className="text-muted small mb-3">
                                            Defines how many questions of each type are generated per blueprint run.
                                            Some standards use nested configuration objects — those are shown as key/value details.
                                        </p>
                                        <Table size="sm" bordered style={{ fontSize: 13 }}>
                                            <thead className="table-light">
                                                <tr>
                                                    <th scope="col">Key / Question Type</th>
                                                    <th scope="col" className="text-center">Count</th>
                                                    <th scope="col">Details / Config</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {assessmentMix.map(({ key, display, detail }, i) => (
                                                    <tr key={i}>
                                                        <td><Badge bg="secondary">{humanise(key)}</Badge></td>
                                                        <td className="text-center fw-bold">
                                                            {display ?? <span className="text-muted small">object</span>}
                                                        </td>
                                                        <td className="text-muted small">
                                                            {detail
                                                                ? <code style={{ fontSize: 11 }}>{detail}</code>
                                                                : key === 'mcq'          ? 'Auto-graded, 4 options, answer key'
                                                                : key === 'short_note'   ? 'Short written answer, 2–5 sentences'
                                                                : key === 'essay'        ? 'Long-form response with rubric'
                                                                : key === 'case_study'   ? 'Scenario-based applied question'
                                                                : key === 'true_false'   ? 'Binary choice question'
                                                                : key === 'short_answer' ? 'One to two sentence factual answer'
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
                <div className="text-muted small">
                    {data?.tenant_id
                        ? 'This is a tenant-specific customisation.'
                        : 'This is a global standard — clone it to customise.'}
                </div>
                <div className="d-flex gap-2">
                    {onClone && data && (
                        <Button variant="outline-primary" size="sm" onClick={() => { onHide(); onClone(data); }}>
                            Clone &amp; Customise →
                        </Button>
                    )}
                    <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
